import { useState } from 'react';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY_HERE';

export const useLocation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const getCurrentLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
        },
        (error) => {
          let errorMessage = 'Unable to retrieve your location';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location permission denied. Please enable location access in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out.';
              break;
            default:
              errorMessage = 'An unknown error occurred.';
          }
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${GOOGLE_MAPS_API_KEY}`
      );

      if (!response.ok) {
        throw new Error('Failed to fetch address from coordinates');
      }

      const data = await response.json();

      if (data.status !== 'OK' || !data.results || data.results.length === 0) {
        throw new Error('No address found for this location');
      }

      // Parse the address components
      const result = data.results[0];
      const addressComponents = result.address_components;

      const getComponent = (types) => {
        const component = addressComponents.find(comp =>
          types.some(type => comp.types.includes(type))
        );
        return component ? component.long_name : '';
      };

      // Extract address parts
      const streetNumber = getComponent(['street_number']);
      const route = getComponent(['route']);
      const premise = getComponent(['premise']);
      const subpremise = getComponent(['subpremise']);

      // Build street address with multiple fallbacks
      let street = '';
      if (streetNumber && route) {
        street = `${streetNumber} ${route}`;
      } else if (route) {
        street = route;
      } else if (streetNumber) {
        street = streetNumber;
      } else if (premise) {
        street = premise;
      } else if (subpremise) {
        street = subpremise;
      } else {
        // Fallback: extract first line from formatted_address
        const formattedParts = result.formatted_address.split(',');
        if (formattedParts.length > 0) {
          street = formattedParts[0].trim();
        }
      }

      const city = getComponent(['locality', 'administrative_area_level_2']) ||
                   getComponent(['sublocality_level_1', 'sublocality']);

      const state = getComponent(['administrative_area_level_1']);

      const zipCode = getComponent(['postal_code']);

      return {
        street: street || '',
        city: city || '',
        state: state || '',
        zipCode: zipCode || '',
        fullAddress: result.formatted_address
      };
    } catch (error) {
      throw new Error('Failed to get address details: ' + error.message);
    }
  };

  const getAddressFromCurrentLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current coordinates
      const coords = await getCurrentLocation();

      // Reverse geocode to get address
      const address = await reverseGeocode(coords.lat, coords.lng);

      setLoading(false);
      return address;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      throw err;
    }
  };

  return {
    getAddressFromCurrentLocation,
    loading,
    error
  };
};

export default useLocation;
